/**
 * Household React Query Hooks
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as householdsApi from "../api/households";
import { ANALYTICS_CONTEXT_QUERY_KEY } from "./use-analytics-context";

export const HOUSEHOLD_QUERY_KEY = "household";
export const INVITE_PREVIEW_QUERY_KEY = "household-invite-preview";

/** The caller's household (null household when solo). */
export function useHousehold() {
  return useQuery({
    queryKey: [HOUSEHOLD_QUERY_KEY],
    queryFn: () => householdsApi.getMyHousehold(),
  });
}

/** Create/rotate the household invite link. */
export function useCreateHouseholdInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => householdsApi.createHouseholdInvite(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [HOUSEHOLD_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [ANALYTICS_CONTEXT_QUERY_KEY],
      });
    },
  });
}

/** Invite preview for the join page. Token comes from the URL. */
export function useInvitePreview(token: string) {
  return useQuery({
    queryKey: [INVITE_PREVIEW_QUERY_KEY, token],
    queryFn: () => householdsApi.getInvitePreview(token),
    retry: false, // an invalid/expired token will never become valid
  });
}

/**
 * Join a household. Membership changes what nearly every server query is
 * allowed to see (plans, grocery lists, pantry, favorites), so invalidate
 * the entire cache rather than enumerating keys.
 */
export function useJoinHousehold() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (token: string) => householdsApi.joinHousehold(token),
    onSuccess: () => {
      queryClient.invalidateQueries();
      queryClient.invalidateQueries({
        queryKey: [ANALYTICS_CONTEXT_QUERY_KEY],
      });
    },
  });
}

/** Leave the household. Same whole-cache invalidation rationale as join. */
export function useLeaveHousehold() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => householdsApi.leaveHousehold(),
    onSuccess: () => {
      queryClient.invalidateQueries();
      queryClient.invalidateQueries({
        queryKey: [ANALYTICS_CONTEXT_QUERY_KEY],
      });
    },
  });
}
